import React from 'react'
import { Link, usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import AppLayout from '@/layouts/AppLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Sparkles, HardDrive, FolderLock, Folder, AlertTriangle } from 'lucide-react'

type Props = {
  plan: string
  isUnlimited: boolean
  storageUsed: number
  storageLimit: number
  storageUsedFormatted: string
  storageLimitFormatted: string
  storagePercentage: number
  storageExceeded: boolean
  repositoryCount: number
  privateRepositoryCount: number
  privateRepositoryLimit: number | null
  privateRepoExceeded: boolean
}

export default function DashboardIndex({
  plan,
  isUnlimited,
  storageUsedFormatted,
  storageLimitFormatted,
  storagePercentage,
  storageExceeded,
  repositoryCount,
  privateRepositoryCount,
  privateRepositoryLimit,
  privateRepoExceeded,
}: Props) {
  const { currentUser, flash } = usePage().props as {
    currentUser: { username: string }
    flash?: { notice?: string; alert?: string }
  }

  // flash notice/alert があればトースト表示
  React.useEffect(() => {
    if (flash?.notice) {
      toast.success(flash.notice)
    }
    if (flash?.alert) {
      toast.error(flash.alert)
    }
  }, [flash?.notice, flash?.alert])

  const isPro = plan === 'Pro'
  const isPaidPlan = isPro || isUnlimited
  const isStorageWarning = !isUnlimited && storagePercentage >= 80
  const isStorageCritical = !isUnlimited && storagePercentage >= 100
  const hasLimitExceeded = storageExceeded || privateRepoExceeded

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* 制限超過警告バナー */}
        {hasLimitExceeded && (
          <Alert variant="destructive" className="border-destructive/50 bg-destructive/10">
            <AlertTriangle className="h-5 w-5" />
            <AlertTitle className="text-lg font-semibold">プラン制限を超過しています</AlertTitle>
            <AlertDescription className="mt-2 space-y-2">
              <div className="flex flex-col gap-1 text-sm">
                {privateRepoExceeded && (
                  <p>
                    プライベートリポジトリ: <span className="font-semibold">{privateRepositoryCount}個</span> / {privateRepositoryLimit}個
                    <span className="text-destructive font-medium ml-2">
                      ({privateRepositoryCount - (privateRepositoryLimit ?? 0)}個超過)
                    </span>
                  </p>
                )}
                {storageExceeded && (
                  <p>
                    ストレージ: <span className="font-semibold">{storageUsedFormatted}</span> / {storageLimitFormatted}
                    <span className="text-destructive font-medium ml-2">(超過)</span>
                  </p>
                )}
              </div>
              <p className="text-sm mt-3">
                超過分のプライベートリポジトリへの push はできません。
                リポジトリを削除・公開に変更するか、Pro プランにアップグレードしてください。
              </p>
              <div className="flex gap-3 mt-4">
                <Link href="/settings/billing">
                  <Button size="sm" className="shadow-md">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Pro にアップグレード
                  </Button>
                </Link>
                <Link href="/repositories">
                  <Button size="sm" variant="outline">
                    リポジトリを管理
                  </Button>
                </Link>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">ダッシュボード</h1>
            <p className="text-muted-foreground text-lg">ようこそ、{currentUser.username} さん</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={isPaidPlan ? 'default' : 'secondary'} className="shadow-sm">
              {plan}
            </Badge>
            {!isPaidPlan && (
              <Link href="/settings/billing">
                <Button size="sm" className="shadow-md shadow-primary/10 hover:shadow-lg hover:shadow-primary/20 transition-all">
                  <Sparkles className="w-4 h-4 mr-2" />
                  アップグレード
                </Button>
              </Link>
            )}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* ストレージ使用量 */}
          <Card className="group border-border/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary/5 hover:border-primary/30 hover:-translate-y-1">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">ストレージ</CardTitle>
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-primary/20 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                <HardDrive className="h-5 w-5 text-primary" />
              </div>
            </CardHeader>
            <CardContent>
              {isUnlimited ? (
                <>
                  <div className="text-2xl font-bold text-primary">
                    {storageUsedFormatted}
                    <span className="text-sm font-normal text-muted-foreground ml-1">
                      / 無制限
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Unlimited プラン
                  </p>
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold text-primary">
                    {storageUsedFormatted}
                    <span className="text-sm font-normal text-muted-foreground ml-1">
                      / {storageLimitFormatted}
                    </span>
                  </div>
                  <Progress
                    value={Math.min(storagePercentage, 100)}
                    className={`mt-2 ${
                      isStorageCritical
                        ? '[&>div]:bg-destructive'
                        : isStorageWarning
                        ? '[&>div]:bg-warning'
                        : '[&>div]:bg-primary'
                    }`}
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    {storagePercentage}% 使用中
                  </p>
                  {isStorageCritical && (
                    <p className="text-xs text-destructive mt-1">
                      ストレージが上限に達しています。push できません。
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* リポジトリ数 */}
          <Card className="group border-border/50 transition-all duration-300 hover:shadow-xl hover:shadow-accent-blue/5 hover:border-accent-blue/30 hover:-translate-y-1">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">リポジトリ</CardTitle>
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-accent-blue/15 to-accent-blue/5 ring-1 ring-accent-blue/20 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                <Folder className="h-5 w-5 text-accent-blue" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-accent-blue">{repositoryCount}</div>
              <p className="text-xs text-muted-foreground mt-2">
                作成済みリポジトリ
              </p>
            </CardContent>
          </Card>

          {/* プライベートリポジトリ */}
          <Card className="group border-border/50 transition-all duration-300 hover:shadow-xl hover:shadow-accent-purple/5 hover:border-accent-purple/30 hover:-translate-y-1">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">プライベートリポジトリ</CardTitle>
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-accent-purple/15 to-accent-purple/5 ring-1 ring-accent-purple/20 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                <FolderLock className="h-5 w-5 text-accent-purple" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-accent-purple">
                {privateRepositoryCount}
                {!isPaidPlan && (
                  <span className="text-sm font-normal text-muted-foreground ml-1">
                    / {privateRepositoryLimit}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {isPaidPlan ? '無制限' : `Free プランは ${privateRepositoryLimit} 個まで`}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* クイックアクション */}
        <Card className="border-border/50 transition-all duration-300 hover:shadow-xl hover:shadow-primary/5 hover:border-primary/20">
          <CardHeader>
            <CardTitle>クイックアクション</CardTitle>
          </CardHeader>
          <CardContent className="flex gap-4">
            <Link href="/repositories/new">
              <Button>新しいリポジトリを作成</Button>
            </Link>
            <Link href="/repositories">
              <Button variant="outline">リポジトリ一覧</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
